import {lazy} from 'react';
import type {RouteObject} from 'react-router';

// Layouts
import AppLayout from './layouts/AppLayout';
import AuthLayout from './layouts/AuthLayout';

// Auth pages
const LoginPage = lazy(() => import('./app/(auth)/login/page'));
const ForgotPasswordPage = lazy(
    () => import('./app/(auth)/forgot-password/page')
);

// Homepage
const HomePage = lazy(() => import('./app/(modelbank)/(homepage)/page'));

// Products
const ProductsPage = lazy(() => import('./app/(modelbank)/products/page'));
const EditCategoriesPage = lazy(
    () => import('./app/(modelbank)/products/edit-categories/page')
);
const ProductRedirectPage = lazy(
    () => import('./app/(modelbank)/products/[id]/page')
);
const ProductDetailsPage = lazy(
    () => import('./app/(modelbank)/products/[id]/product-details/page')
);
const Product3DModelPage = lazy(
    () => import('./app/(modelbank)/products/[id]/3d-model/page')
);
const ProductRendersPage = lazy(
    () => import('./app/(modelbank)/products/[id]/renders/page')
);
const ProductMediaPage = lazy(
    () => import('./app/(modelbank)/products/[id]/media/page')
);
const ProductMediaFilterPage = lazy(
    () => import('./app/(modelbank)/products/[id]/media/[filter]/page')
);
const ProductConfiguratorPage = lazy(
    () => import('./app/(modelbank)/products/[id]/configurator/page')
);
const ProductExternalViewerPage = lazy(
    () => import('./app/(modelbank)/products/[id]/external-viewer/page')
);
const ProductAILifestylePage = lazy(
    () => import('./app/(modelbank)/products/[id]/ai-lifestyle/page')
);
const ProductLindaCardsPage = lazy(
    () => import('./app/(modelbank)/products/[id]/linda-cards/page')
);

// Groups
const GroupsPage = lazy(() => import('./app/(modelbank)/groups/page'));
const GroupRedirectPage = lazy(
    () => import('./app/(modelbank)/groups/[id]/page')
);
const GroupDetailsPage = lazy(
    () => import('./app/(modelbank)/groups/[id]/group-details/page')
);
const GroupMediaPage = lazy(
    () => import('./app/(modelbank)/groups/[id]/media/page')
);
const GroupMediaFilterPage = lazy(
    () => import('./app/(modelbank)/groups/[id]/media/[filter]/page')
);
const GroupRendersPage = lazy(
    () => import('./app/(modelbank)/groups/[id]/renders/page')
);
const GroupExternalViewerPage = lazy(
    () => import('./app/(modelbank)/groups/[id]/external-viewer/page')
);
const GroupAILifestylePage = lazy(
    () => import('./app/(modelbank)/groups/[id]/ai-lifestyle/page')
);

// Materials
const MaterialsPage = lazy(() => import('./app/(modelbank)/materials/page'));
const MaterialRedirectPage = lazy(
    () => import('./app/(modelbank)/materials/[id]/page')
);
const MaterialDetailsPage = lazy(
    () => import('./app/(modelbank)/materials/[id]/material-details/page')
);
const MaterialEditPage = lazy(
    () => import('./app/(modelbank)/materials/[id]/edit/page')
);
const TextureMapsPage = lazy(
    () => import('./app/(modelbank)/materials/[id]/texture-maps/page')
);
const DynamicModelsPage = lazy(
    () => import('./app/(modelbank)/materials/[id]/dynamic-models/page')
);

// Modular Systems
const ModularSystemsPage = lazy(
    () => import('./app/(modelbank)/modular-systems/page')
);
const MSRedirectPage = lazy(
    () => import('./app/(modelbank)/modular-systems/[id]/page')
);
const MSDetailsPage = lazy(
    () => import('./app/(modelbank)/modular-systems/[id]/details/page')
);
const MSModelsPage = lazy(
    () => import('./app/(modelbank)/modular-systems/[id]/models/page')
);
const MSConfigurationsPage = lazy(
    () => import('./app/(modelbank)/modular-systems/[id]/configurations/page')
);
const MSConfiguratorPage = lazy(
    () => import('./app/(modelbank)/modular-systems/[id]/configurator/page')
);
const MSExternalConfiguratorPage = lazy(
    () =>
        import('./app/(modelbank)/modular-systems/[id]/external-configurator/page')
);
const MSPresetsPage = lazy(
    () => import('./app/(modelbank)/modular-systems/[id]/presets/page')
);

// Linda
const LindaPage = lazy(() => import('./app/(modelbank)/linda/page'));
const LindaDetailPage = lazy(() => import('./app/(modelbank)/linda/[id]/page'));
const LindaActivityPage = lazy(
    () => import('./app/(modelbank)/linda/[id]/activity/page')
);
const LindaAnalyticsPage = lazy(
    () => import('./app/(modelbank)/linda/[id]/analytics/page')
);
const LindaPlaygroundPage = lazy(
    () => import('./app/(modelbank)/linda/[id]/playground/page')
);
const LindaSettingsPage = lazy(
    () => import('./app/(modelbank)/linda/[id]/settings/page')
);
const LindaSourcesPage = lazy(
    () => import('./app/(modelbank)/linda/[id]/sources/page')
);

// Other
const FloorWallMaterialsPage = lazy(
    () => import('./app/(modelbank)/floor-wall-materials/page')
);
const FloorWallMaterialsImportPage = lazy(
    () => import('./app/(modelbank)/floor-wall-materials/import/page')
);
const JobsPage = lazy(() => import('./app/(modelbank)/jobs/page'));
const JobDetailPage = lazy(() => import('./app/(modelbank)/jobs/[id]/page'));
const BatchRenderPage = lazy(
    () => import('./app/(modelbank)/batch-render/page')
);
const BatchRenderDetailPage = lazy(
    () => import('./app/(modelbank)/batch-render/[id]/page')
);
const CreateBatchRenderPage = lazy(
    () => import('./app/(modelbank)/batch-render/create/page')
);
const ReviewTasksPage = lazy(
    () => import('./app/(modelbank)/review-tasks/page')
);
const ImportModelsPage = lazy(
    () => import('./app/(modelbank)/import-models/page')
);
const CreateImportModelsPage = lazy(
    () => import('./app/(modelbank)/import-models/create/page')
);
const ImportMaterialsPage = lazy(
    () => import('./app/(modelbank)/import-materials/page')
);
const CreateImportMaterialsPage = lazy(
    () => import('./app/(modelbank)/import-materials/create/page')
);

// Not found
const NotFound = lazy(() => import('./components/display/not-found'));

export const routes: RouteObject[] = [
    // Auth routes
    {
        element: <AuthLayout />,
        children: [
            {path: '/login', element: <LoginPage />},
            {path: '/forgot-password', element: <ForgotPasswordPage />},
        ],
    },
    // App routes
    {
        element: <AppLayout />,
        children: [
            {path: '/', element: <HomePage />},
            // Products
            {path: '/products', element: <ProductsPage />},
            {
                path: '/products/edit-categories',
                element: <EditCategoriesPage />,
            },
            {path: '/products/:id', element: <ProductRedirectPage />},
            {
                path: '/products/:id/product-details',
                element: <ProductDetailsPage />,
            },
            {
                path: '/products/:id/3d-model',
                element: <Product3DModelPage />,
            },
            {
                path: '/products/:id/renders',
                element: <ProductRendersPage />,
            },
            {path: '/products/:id/media', element: <ProductMediaPage />},
            {
                path: '/products/:id/media/:filter',
                element: <ProductMediaFilterPage />,
            },
            {
                path: '/products/:id/configurator',
                element: <ProductConfiguratorPage />,
            },
            {
                path: '/products/:id/external-viewer',
                element: <ProductExternalViewerPage />,
            },
            {
                path: '/products/:id/ai-lifestyle',
                element: <ProductAILifestylePage />,
            },
            {
                path: '/products/:id/linda-cards',
                element: <ProductLindaCardsPage />,
            },
            // Groups
            {path: '/groups', element: <GroupsPage />},
            {path: '/groups/:id', element: <GroupRedirectPage />},
            {
                path: '/groups/:id/group-details',
                element: <GroupDetailsPage />,
            },
            {path: '/groups/:id/media', element: <GroupMediaPage />},
            {
                path: '/groups/:id/media/:filter',
                element: <GroupMediaFilterPage />,
            },
            {
                path: '/groups/:id/renders',
                element: <GroupRendersPage />,
            },
            {
                path: '/groups/:id/external-viewer',
                element: <GroupExternalViewerPage />,
            },
            {
                path: '/groups/:id/ai-lifestyle',
                element: <GroupAILifestylePage />,
            },
            // Materials
            {path: '/materials', element: <MaterialsPage />},
            {path: '/materials/:id', element: <MaterialRedirectPage />},
            {
                path: '/materials/:id/material-details',
                element: <MaterialDetailsPage />,
            },
            {path: '/materials/:id/edit', element: <MaterialEditPage />},
            {
                path: '/materials/:id/texture-maps',
                element: <TextureMapsPage />,
            },
            {
                path: '/materials/:id/dynamic-models',
                element: <DynamicModelsPage />,
            },
            // Modular Systems
            {path: '/modular-systems', element: <ModularSystemsPage />},
            {
                path: '/modular-systems/:id',
                element: <MSRedirectPage />,
            },
            {
                path: '/modular-systems/:id/details',
                element: <MSDetailsPage />,
            },
            {
                path: '/modular-systems/:id/models',
                element: <MSModelsPage />,
            },
            {
                path: '/modular-systems/:id/configurations',
                element: <MSConfigurationsPage />,
            },
            {
                path: '/modular-systems/:id/configurator',
                element: <MSConfiguratorPage />,
            },
            {
                path: '/modular-systems/:id/external-configurator',
                element: <MSExternalConfiguratorPage />,
            },
            {
                path: '/modular-systems/:id/presets',
                element: <MSPresetsPage />,
            },
            // Linda
            {path: '/linda', element: <LindaPage />},
            {path: '/linda/:id', element: <LindaDetailPage />},
            {
                path: '/linda/:id/activity',
                element: <LindaActivityPage />,
            },
            {
                path: '/linda/:id/analytics',
                element: <LindaAnalyticsPage />,
            },
            {
                path: '/linda/:id/playground',
                element: <LindaPlaygroundPage />,
            },
            {
                path: '/linda/:id/settings',
                element: <LindaSettingsPage />,
            },
            {
                path: '/linda/:id/sources',
                element: <LindaSourcesPage />,
            },
            // Other
            {
                path: '/floor-wall-materials',
                element: <FloorWallMaterialsPage />,
            },
            {
                path: '/floor-wall-materials/import',
                element: <FloorWallMaterialsImportPage />,
            },
            {path: '/jobs', element: <JobsPage />},
            {path: '/jobs/:id', element: <JobDetailPage />},
            {path: '/import-models', element: <ImportModelsPage />},
            {
                path: '/import-models/create',
                element: <CreateImportModelsPage />,
            },
            {path: '/import-materials', element: <ImportMaterialsPage />},
            {
                path: '/import-materials/create',
                element: <CreateImportMaterialsPage />,
            },
            {path: '/batch-render', element: <BatchRenderPage />},
            {
                path: '/batch-render/create',
                element: <CreateBatchRenderPage />,
            },
            {path: '/batch-render/:id', element: <BatchRenderDetailPage />},
            {path: '/review-tasks', element: <ReviewTasksPage />},
            // Catch-all
            {path: '*', element: <NotFound />},
        ],
    },
];
